import type { UpstoxCandle } from '../services/candle-service'

export interface HistoricalLevels {
    prevMonth: { high: number; low: number; close: number } | null
    prevWeek: { high: number; low: number; close: number } | null
    prevDay: { high: number; low: number; close: number } | null
}

export interface IntradayLevels {
    first5Min: { high: number; low: number; close: number } | null
    first15Min: { high: number; low: number; close: number } | null
}

export interface IndicatorLevels {
    prev2Months?: { high: number; low: number; close: number } | null
    prevMonth?: { high: number; low: number; close: number } | null
    prevWeek?: { high: number; low: number; close: number } | null
    prevDay?: { high: number; low: number; close: number } | null
    first5Min?: { high: number; low: number; close: number } | null
    first15Min?: { high: number; low: number; close: number } | null
    todayOpen?: number
    todayHigh?: number
    todayLow?: number
    rsi5m?: number | null
}

export const calculateRSI = (candles: UpstoxCandle[], period: number = 14): number | null => {
    if (!candles || candles.length < period + 1) return null;

    let sumGain = 0;
    let sumLoss = 0;

    // Calculate initial average gain/loss
    for (let i = 1; i <= period; i++) {
        const current = candles[i];
        const previous = candles[i - 1];
        if (!current || !previous) continue;
        const change = current.close - previous.close;
        if (change > 0) sumGain += change;
        else sumLoss -= change;
    }

    let avgGain = sumGain / period;
    let avgLoss = sumLoss / period;

    // Calculate smoothed technical RSI across the rest of the array (Standard Wilder's Smoothing)
    for (let i = period + 1; i < candles.length; i++) {
        const current = candles[i];
        const previous = candles[i - 1];
        if (!current || !previous) continue;
        
        const change = current.close - previous.close;
        const gain = change > 0 ? change : 0;
        const loss = change < 0 ? -change : 0;
        
        avgGain = ((avgGain * (period - 1)) + gain) / period;
        avgLoss = ((avgLoss * (period - 1)) + loss) / period;
    }

    if (avgLoss === 0) return 100;
    
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
};

export const calculateHistoricalLevels = (candles: UpstoxCandle[]): HistoricalLevels => {
    if (!candles.length) return { prevMonth: null, prevWeek: null, prevDay: null }

    const now = new Date()
    // Helper to get previous full period data
    // Assuming 'candles' contains daily candles if we fetch long history?
    // User requested "fetch last 10 days + current day history data" at minute level.
    // Calculating Monthly/Weekly from 1 minute data of just 10 days is impossible for "Previous Month".
    // We strictly need DAILY data for proper Prev Month/Week calculation if we want accuracy.
    // However, the user prompt implies fetching "as per api docs monthly, weekly and daily".
    // So we should probably fetch valid historical aggregates separately or rely on what we have.
    // Let's implement logic assuming we might have to fetch separate candles or aggregate what we have.

    // For now, based on "fetch last 10 days", we likely CANNOT determine Prev Month from just 10 days of 1-min data.
    // But the user said: "fetch previous month, week, day data as per api docs monthly, weekly and daily".
    // Use this function to process those specifically fetched arrays.

    // Wait, the prompt says: "fetch previous month, week, day data as per api docs monthly, weekly and daily then crate the indicator"
    // This implies we will fetch separate datasets for Month, Week, Day.

    // So this function should take THOSE specific arrays.

    return {
        prevMonth: null, // To be populated from specific Monthly fetch
        prevWeek: null, // To be populated from specific Weekly fetch
        prevDay: null, // To be populated from specific Daily fetch
    }
}

export const extractOHLC = (
    candles: UpstoxCandle[],
    periodIndex: number = 1,
): { high: number; low: number; close: number } | null => {
    // periodIndex = 1 means "Previous" (0 is current, 1 is previous completed)
    if (candles.length <= periodIndex) return null
    // Candles from API come Latest First (index 0 is current/latest).
    // Our service reverses them (ascending), so Latest is LAST.
    // Let's assume the service returns Ascending (Oldest -> Newest).
    // Then Previous is `length - 2` (if current is present) or `length - 1` (if current not finished?)
    // Standard daily/weekly/monthly API usually returns completed candles + developing current.

    const prev = candles[candles.length - 1 - periodIndex]
    if (!prev) return null
    return { high: prev.high, low: prev.low, close: prev.close }
}

export const calculateIntradayLevels = (todayCandles: UpstoxCandle[]): IntradayLevels => {
    // todayCandles should be 1-minute candles for CURRENT DAY only
    if (todayCandles.length < 5) return { first5Min: null, first15Min: null }

    // First 5 Min
    const first5 = todayCandles.slice(0, 5)
    const high5 = first5.length > 0 ? first5.reduce((max, c) => Math.max(max, c.high), -Infinity) : 0
    const low5 = first5.length > 0 ? first5.reduce((min, c) => Math.min(min, c.low), Infinity) : 0
    const close5 = first5[4]?.close || 0

    // First 15 Min
    let first15Min: { high: number; low: number; close: number } | null = null
    if (todayCandles.length >= 15) {
        const first15 = todayCandles.slice(0, 15)
        const high15 = first15.length > 0 ? first15.reduce((max, c) => Math.max(max, c.high), -Infinity) : 0
        const low15 = first15.length > 0 ? first15.reduce((min, c) => Math.min(min, c.low), Infinity) : 0
        const close15 = first15[14]?.close || 0
        first15Min = { high: high15, low: low15, close: close15 }
    }

    return {
        first5Min: { high: high5, low: low5, close: close5 },
        first15Min: first15Min,
    }
}

export const aggregate1mTo5m = (minuteCandles: UpstoxCandle[]): UpstoxCandle[] => {
    if (!minuteCandles || minuteCandles.length === 0) return [];
    
    const m5Candles: UpstoxCandle[] = [];
    let currentM5: UpstoxCandle | null = null;
    
    for (const c of minuteCandles) {
        // Market starts at 09:15, so 09:15-09:19 is one bucket.
        const d = new Date(c.timestamp);
        const minutes = d.getHours() * 60 + d.getMinutes();
        const marketStart = 9 * 60 + 15;
        
        let bucketIndex = 0;
        if (minutes >= marketStart) {
            bucketIndex = Math.floor((minutes - marketStart) / 5);
        } else {
            bucketIndex = -1; // Pre-market or something
        }
        
        const bucketStr = d.toISOString().split('T')[0] + '-' + bucketIndex;
        
        if (!currentM5 || (currentM5 as any)._bucket !== bucketStr) {
            if (currentM5) {
                delete (currentM5 as any)._bucket;
                m5Candles.push(currentM5);
            }
            currentM5 = {
                timestamp: c.timestamp, // use start of bucket timestamp
                open: c.open,
                high: c.high,
                low: c.low,
                close: c.close,
                volume: c.volume,
                oi: c.oi,
                ...( { _bucket: bucketStr } ) // temporary field
            } as UpstoxCandle;
        } else {
            currentM5.high = Math.max(currentM5.high, c.high);
            currentM5.low = Math.min(currentM5.low, c.low);
            currentM5.close = c.close;
            currentM5.volume += c.volume;
            currentM5.oi = c.oi; // Last OI
        }
    }
    if (currentM5) {
        delete (currentM5 as any)._bucket;
        m5Candles.push(currentM5);
    }
    return m5Candles;
}

